
import { AuditFaultTypeModel, AuditModel, PermissionTypeEnum, QueryOperator, TenantUserModel } from '@/libs/Api';
import { CrudAction, CrudReponse } from '@/libs/core/+state/models/crud-action';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import Page from '@/Page.vue';
import { Component, Prop, Ref, Watch } from 'vue-property-decorator';
import { auditsStore } from '../+state/store';
import { Guid } from '@/libs/common/functions/guid';
import { cloneDeep, isEqual } from 'lodash';
import { authStore } from '@/libs/auth/+store/store';
import { AuthGetter } from '@/libs/auth/models/auth-state';
import ShiftTypeSelect from '@/libs/common/components-smart/ShiftTypeSelect.vue';
import { createCrudQueryPayload } from '@/libs/core/+state/models/crud-query-payload';
import { auditFaultTypesStore } from '@/libs/audit-fault-types/+state/store';

@Component({
    components: { ShiftTypeSelect },
})
export default class Plan extends Page {
    @Prop() tenantId!: string;
    @Prop() auditId!: string;
    audit: AuditModel | null = null;
    auditOriginal: AuditModel | null = null;
    basicRules = [(v: any) => !!v || this.$t('validation.thisFieldIsRequired')];
    deleteDialog = false;
    deleting = false;
    valid = false;
    loading = true;
    saving = false;
    hasChanges = false;
    tenantUser: TenantUserModel = authStore.useGetter(AuthGetter.GetTenantUser) as TenantUserModel;
    auditFaultTypes: AuditFaultTypeModel[] = [];
    auditFaultTypesOriginal: AuditFaultTypeModel[] = [];
    tableHeaders = [
        { text: this.$i18n.t('faultTypes.name'), value: 'name', align: 'left', sortable: false },
        { text: this.$i18n.t('faultTypes.color'), value: 'name', align: 'left', sortable: false },
        { text: this.$i18n.t('faultTypes.priority'), value: 'name', align: 'left', sortable: false },
        { text: this.$i18n.t('faultTypes.isWorkplaceShutdown'), value: 'name', align: 'left', sortable: false },
        { text: this.$i18n.t('faultTypes.isWorkplaceShutdownWhenStationShutdown'), value: 'name', align: 'left', sortable: false },
        { text: '', value: 'id', align: 'left', sortable: false },
    ];

    @Ref() form!: any;

    get isReadonly(): boolean {
        return (
            !this.hasPermissions([PermissionTypeEnum.EditAllAudits]) &&
            !(this.hasPermissions([PermissionTypeEnum.EditOwnedAudit]) && this.audit?.ownerId == this.tenantUser.id)
        );
    }

    @Watch('audit', { deep: true })
    @Watch('auditFaultTypes', { deep: true })
    questionGroupsChanged() {
        this.hasChanges = !isEqual(this.audit, this.auditOriginal);
        if (!this.hasChanges) {
            this.hasChanges = !isEqual(this.auditFaultTypes, this.auditFaultTypesOriginal);
        }
    }

    mounted() {
        this.audit = { ...auditsStore.useGetter(CrudGetter.Detail) } as AuditModel;
        this.auditOriginal = cloneDeep(this.audit);
        this.loadItems().then();
        this.loading = false;
    }

    public getAuditFaultTypes(): Promise<AuditFaultTypeModel[]> {
        return new Promise<AuditFaultTypeModel[]>((resolve, reject) => {
            auditFaultTypesStore.dispatch(
                CrudAction.GetAll,
                createCrudQueryPayload({ field: 'name', index: 1, order: 'asc' }, [
                    {
                        field: 'auditId',
                        op: QueryOperator.Eq,
                        comparand: this.auditId,
                    },
                ])
            );
            this.subscribe(auditFaultTypesStore, CrudReponse.GetAll)
                .then((existingAuditFaultTypes: AuditFaultTypeModel[]) => {
                    resolve(existingAuditFaultTypes);
                })
                .catch((e: any) => {
                    reject(e);
                });
        });
    }

    public deleteAllPromise(payload: AuditFaultTypeModel[]): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            this.deleteAllPromisePop(payload, resolve, reject);
        });
    }

    public saveAllPromise(payload: AuditFaultTypeModel[]): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            this.saveAllPromisePop(payload, resolve, reject);
        });
    }

    protected addAuditFaultType() {
        this.auditFaultTypes.push({
            id: Guid.EmptyGuid(),
            auditId: this.auditId,
            name: '',
            isActive: true,
            isWorkplaceShutdown: false,
            isWorkplaceShutdownWhenStationShutdown: false
        });
    }

    protected deleteAuditFaultType(e: AuditFaultTypeModel) {
        this.auditFaultTypes = this.auditFaultTypes.filter((x) => x != e);
    }

    private save() {
        this.saving = true;
        auditsStore.dispatch(CrudAction.Update, { item: this.audit });
        this.subscribe(auditsStore, CrudReponse.Update)
            .then((e: AuditModel) => {
                this.audit = e;
                this.getAuditFaultTypes()
                    .then((existingAuditFaultTypes: AuditFaultTypeModel[]) => {
                        let auditFaultTypes = this.auditFaultTypes.filter((x) => x != null && (x!.name ?? '').length > 0);
                        if (!this.audit!.useFaultTypes) {
                            auditFaultTypes = [];
                        }
                        const auditFaultTypesToDelete = existingAuditFaultTypes.filter(
                            (x) => auditFaultTypes.find((n) => n.id == x.id) == null
                        );
                        const auditFaultTypesToSave = auditFaultTypes;
                        this.deleteAllPromise(auditFaultTypesToDelete)
                            .then(() => {
                                this.saveAllPromise(auditFaultTypesToSave)
                                    .then(() => {
                                        this.loadItems()
                                            .then(() => {
                                                this.saving = false;
                                            })
                                            .catch((e: any) => {
                                                this.saving = false;
                                                console.error(e);
                                            });
                                    })
                                    .catch((e: any) => {
                                        this.saving = false;
                                        console.error(e);
                                    });
                            })
                            .catch((e: any) => {
                                this.saving = false;
                                console.error(e);
                            });
                    })
                    .catch((e: any) => {
                        this.saving = false;
                        console.error(e);
                    });
            })
            .catch((e: any) => {
                this.saving = false;
                console.error(e);
            });
    }

    private loadItems(): Promise<void> {
        return new Promise((resolve, reject) => {
            this.getAuditFaultTypes()
                .then((auditFaultTypes: AuditFaultTypeModel[]) => {
                    this.auditFaultTypes = [...auditFaultTypes];
                    this.auditFaultTypesOriginal = cloneDeep(this.auditFaultTypes);
                    resolve(undefined);
                })
                .catch((e: any) => reject(e));
        });
    }

    private deleteAllPromisePop(payload: AuditFaultTypeModel[], resolve: (value: any) => void, reject: (reason?: any) => void) {
        if (payload.length > 0) {
            const payloadItem = payload[0];
            if (payloadItem.id != Guid.EmptyGuid()) {
                auditFaultTypesStore.dispatch(CrudAction.Delete, { id: payloadItem.id });
                this.subscribe(auditFaultTypesStore, CrudReponse.Delete)
                    .then(() => {
                        payload.shift();
                        this.deleteAllPromisePop(payload, resolve, reject);
                    })
                    .catch((e: any) => {
                        reject(e);
                    });
            }
        } else {
            resolve(null);
        }
    }

    private saveAllPromisePop(payload: AuditFaultTypeModel[], resolve: (value: any) => void, reject: (reason?: any) => void) {
        if (payload.length > 0) {
            const payloadItem = payload[0];
            if (!payloadItem.isWorkplaceShutdown) {
                payloadItem.isWorkplaceShutdownWhenStationShutdown = false;
            }
            if (payloadItem.id == Guid.EmptyGuid()) {
                auditFaultTypesStore.dispatch(CrudAction.Create, { item: payloadItem });
                this.subscribe(auditFaultTypesStore, CrudReponse.Create)
                    .then(() => {
                        payload.shift();
                        this.saveAllPromisePop(payload, resolve, reject);
                    })
                    .catch((e: any) => {
                        reject(e);
                    });
            } else {
                auditFaultTypesStore.dispatch(CrudAction.Update, { item: payloadItem });
                this.subscribe(auditFaultTypesStore, CrudReponse.Update)
                    .then(() => {
                        payload.shift();
                        this.saveAllPromisePop(payload, resolve, reject);
                    })
                    .catch((e: any) => {
                        reject(e);
                    });
            }
        } else {
            resolve(null);
        }
    }
}
